import styled from 'styled-components';

import { type TransactionsFilterInput, useGetTransactionsQuery } from 'data/__generated__';

import { DataTable } from 'components/DataTable';

import { transactionsColumns, type TransactionRow } from 'pages/Transactions/constants/transactionsColumns';

// The site column is redundant here: the table is already scoped to the current site.
const siteTransactionsColumns = transactionsColumns.filter(
    (column) => !('accessorKey' in column && column.accessorKey === 'siteName'),
);

export const SiteTransactionsSection = ({ siteId }: { siteId: string }) => {
    const siteScopedFilters: Partial<TransactionsFilterInput> = { siteIds: [siteId] };

    const {
        loading: transactionsLoading,
        data: transactionsData,
        error: transactionsError,
        refetch,
        fetchMore,
    } = useGetTransactionsQuery({
        // `sortBy` / `sortOrder` sont dans les `keyArgs` de la field policy `transactions` : les omettre ici
        // alors que le scroll infini les envoie ferait écrire `fetchMore` dans une AUTRE entrée de cache que
        // celle lue par le composant — pages chargées, rien qui s'ajoute. Ils doivent refléter le tri initial.
        variables: { filters: siteScopedFilters, sortBy: 'createdAt', sortOrder: 'desc' },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    // Inline error: the section must not take down the rest of the site page.
    if (transactionsError) {
        return <ErrorMessage>Une erreur est survenue lors de la récupération des transactions du site</ErrorMessage>;
    }

    const hasMore = transactionsData?.transactions.hasMore ?? false;
    const transactions = transactionsData?.transactions.transactions ?? [];

    return (
        <TableContainer>
            <DataTable<TransactionRow, TransactionsFilterInput>
                columns={siteTransactionsColumns}
                data={transactions}
                filterMode="server"
                // Scope de la section : l'utilisateur peut affiner, jamais sortir du site courant.
                pinnedFilters={siteScopedFilters}
                getRowId={(row) => row._id}
                getRowLink={(row) => `/transaction/${row._id}`}
                hasMore={hasMore}
                isLoading={transactionsLoading}
                refetch={refetch}
                fetchMore={fetchMore}
                defaultSortBy="createdAt"
                defaultSortOrder="desc"
                searchPlaceholder="ID de la transaction"
                searchFilterKey="searchValue"
                disableUrlSync
                autoFocusSearch={false}
                emptyMessage="Aucune transaction sur ce site"
            />
        </TableContainer>
    );
};

// Bounded height so the table scrolls internally and its infinite scroll can trigger.
const TableContainer = styled.div`
    display: flex;
    height: 60vh;
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
`;
